import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { alertsApi } from '../../shared/services/alertsApi.js';
import { 
  StatusResponse, 
  Event, 
  Insight, 
  HeatmapCell, 
  GovernanceCallout,
  ExportResult,
  PeerBenchmark,
  DrilldownLink,
  CompliancePack
} from '../../shared/types/executiveDashboard.js';
import { RootState } from '../index.js';

// Define state types
interface DataState<T> {
  data: T;
  loading: boolean;
  error: string | null;
}

interface DashboardState {
  status: DataState<StatusResponse | null>;
  events: DataState<Event[]>;
  insights: DataState<Insight[]>;
  heatmap: DataState<HeatmapCell[]>;
  callouts: DataState<GovernanceCallout[]>;
  exportPacket: DataState<ExportResult[]>;
  peerBenchmark: DataState<PeerBenchmark[]>;
  drilldownLinks: DataState<DrilldownLink[]>;
  compliancePacks: DataState<CompliancePack[]>;
  briefMode: boolean;
}

// Initial state
const initialState: DashboardState = {
  status: { data: null, loading: false, error: null },
  events: { data: [], loading: false, error: null },
  insights: { data: [], loading: false, error: null },
  heatmap: { data: [], loading: false, error: null },
  callouts: { data: [], loading: false, error: null },
  exportPacket: { data: [], loading: false, error: null },
  peerBenchmark: { data: [], loading: false, error: null },
  drilldownLinks: { data: [], loading: false, error: null },
  compliancePacks: { data: [], loading: false, error: null },
  briefMode: false,
};

// Individual thunks for each endpoint
export const fetchStatus = createAsyncThunk<StatusResponse, string>(
  'dashboard/fetchStatus',
  async (tenant_id: string) => {
    return await alertsApi.getStatus(tenant_id);
  }
);

export const fetchEvents = createAsyncThunk<Event[], string>(
  'dashboard/fetchEvents',
  async (tenant_id: string) => {
    return await alertsApi.getEvents(tenant_id);
  }
);

export const fetchInsights = createAsyncThunk<Insight[], string>(
  'dashboard/fetchInsights',
  async (tenant_id: string) => {
    return await alertsApi.getInsights(tenant_id);
  }
);

export const fetchHeatmap = createAsyncThunk<HeatmapCell[], string>(
  'dashboard/fetchHeatmap',
  async (tenant_id: string) => {
    return await alertsApi.getHeatmap(tenant_id);
  }
);

export const fetchCallouts = createAsyncThunk<GovernanceCallout[], string>(
  'dashboard/fetchCallouts',
  async (tenant_id: string) => {
    return await alertsApi.getCallouts(tenant_id);
  }
);

export const fetchExportPacket = createAsyncThunk<ExportResult[], string>(
  'dashboard/fetchExportPacket',
  async (tenant_id: string) => {
    return await alertsApi.getExportPacket(tenant_id);
  }
);

export const fetchPeerBenchmark = createAsyncThunk<PeerBenchmark[], string>(
  'dashboard/fetchPeerBenchmark',
  async (tenant_id: string) => {
    return await alertsApi.getPeerBenchmark(tenant_id);
  }
);

export const fetchDrilldownLinks = createAsyncThunk<DrilldownLink[], string>(
  'dashboard/fetchDrilldownLinks',
  async (tenant_id: string) => {
    return await alertsApi.getDrilldownLinks(tenant_id);
  }
);

export const fetchCompliancePacks = createAsyncThunk<CompliancePack[], string>(
  'dashboard/fetchCompliancePacks',
  async (tenant_id: string) => {
    return await alertsApi.getCompliancePacks(tenant_id);
  }
);

// Master thunk to fetch all dashboard data
export const fetchDashboardData = createAsyncThunk(
  'dashboard/fetchAll',
  async (tenant_id: string, { dispatch }) => {
    await Promise.all([
      dispatch(fetchStatus(tenant_id)),
      dispatch(fetchEvents(tenant_id)),
      dispatch(fetchInsights(tenant_id)),
      dispatch(fetchHeatmap(tenant_id)),
      dispatch(fetchCallouts(tenant_id)),
    ]);
  }
);

// Master thunk to fetch all dashboard extras
export const fetchDashboardExtras = createAsyncThunk(
  'dashboard/fetchExtras',
  async (tenant_id: string, { dispatch }) => {
    await Promise.all([
      dispatch(fetchExportPacket(tenant_id)),
      dispatch(fetchPeerBenchmark(tenant_id)),
      dispatch(fetchDrilldownLinks(tenant_id)),
      dispatch(fetchCompliancePacks(tenant_id)),
    ]);
  }
);

// Dashboard slice
const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearDashboardData(state) {
      state.status = { data: null, loading: false, error: null };
      state.events = { data: [], loading: false, error: null };
      state.insights = { data: [], loading: false, error: null };
      state.heatmap = { data: [], loading: false, error: null };
      state.callouts = { data: [], loading: false, error: null };
      state.exportPacket = { data: [], loading: false, error: null };
      state.peerBenchmark = { data: [], loading: false, error: null };
      state.drilldownLinks = { data: [], loading: false, error: null };
      state.compliancePacks = { data: [], loading: false, error: null };
    },
    toggleBriefMode(state) {
      state.briefMode = !state.briefMode;
    },
    setBriefMode(state, action: PayloadAction<boolean>) {
      state.briefMode = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Export Packet reducers
    builder
      .addCase(fetchExportPacket.pending, (state) => {
        state.exportPacket.loading = true;
        state.exportPacket.error = null;
      })
      .addCase(fetchExportPacket.fulfilled, (state, action) => {
        state.exportPacket.loading = false;
        state.exportPacket.data = action.payload;
      })
      .addCase(fetchExportPacket.rejected, (state, action) => {
        state.exportPacket.loading = false;
        console.error('Export packet fetch error (handled with fallback):', action.error?.message);
      });

    // Peer Benchmark reducers
    builder
      .addCase(fetchPeerBenchmark.pending, (state) => {
        state.peerBenchmark.loading = true;
        state.peerBenchmark.error = null;
      })
      .addCase(fetchPeerBenchmark.fulfilled, (state, action) => {
        state.peerBenchmark.loading = false;
        state.peerBenchmark.data = action.payload;
      })
      .addCase(fetchPeerBenchmark.rejected, (state, action) => {
        state.peerBenchmark.loading = false;
        console.error('Peer benchmark fetch error (handled with fallback):', action.error?.message);
      });

    // Drilldown Links reducers
    builder
      .addCase(fetchDrilldownLinks.pending, (state) => {
        state.drilldownLinks.loading = true;
        state.drilldownLinks.error = null;
      })
      .addCase(fetchDrilldownLinks.fulfilled, (state, action) => {
        state.drilldownLinks.loading = false;
        state.drilldownLinks.data = action.payload;
      })
      .addCase(fetchDrilldownLinks.rejected, (state, action) => {
        state.drilldownLinks.loading = false;
        console.error('Drilldown links fetch error (handled with fallback):', action.error?.message);
      });

    // Compliance Packs reducers
    builder
      .addCase(fetchCompliancePacks.pending, (state) => {
        state.compliancePacks.loading = true;
        state.compliancePacks.error = null;
      })
      .addCase(fetchCompliancePacks.fulfilled, (state, action) => {
        state.compliancePacks.loading = false;
        state.compliancePacks.data = action.payload;
      })
      .addCase(fetchCompliancePacks.rejected, (state, action) => {
        state.compliancePacks.loading = false;
        console.error('Compliance packs fetch error (handled with fallback):', action.error.message);
      });

    // Status
    builder.addCase(fetchStatus.pending, (state) => {
      state.status.loading = true;
      state.status.error = null;
    });
    builder.addCase(fetchStatus.fulfilled, (state, action) => {
      state.status.loading = false;
      state.status.data = action.payload;
      state.status.error = null; // Clear any previous errors
    });
    builder.addCase(fetchStatus.rejected, (state, action) => {
      state.status.loading = false;
      console.error('Status fetch error (handled with fallback):', action.error.message);
    });

    // Events
    builder.addCase(fetchEvents.pending, (state) => {
      state.events.loading = true;
      state.events.error = null;
    });
    builder.addCase(fetchEvents.fulfilled, (state, action) => {
      state.events.loading = false;
      state.events.data = action.payload;
    });
    builder.addCase(fetchEvents.rejected, (state, action) => {
      state.events.loading = false;
      console.error('Events fetch error (handled with fallback):', action.error.message);
    });

    // Insights
    builder.addCase(fetchInsights.pending, (state) => {
      state.insights.loading = true;
      state.insights.error = null;
    });
    builder.addCase(fetchInsights.fulfilled, (state, action) => {
      state.insights.loading = false;
      state.insights.data = action.payload;
    });
    builder.addCase(fetchInsights.rejected, (state, action) => {
      state.insights.loading = false;
      console.error('Insights fetch error (handled with fallback):', action.error?.message);
    });

    // Heatmap
    builder.addCase(fetchHeatmap.pending, (state) => {
      state.heatmap.loading = true;
      state.heatmap.error = null;
    });
    builder.addCase(fetchHeatmap.fulfilled, (state, action) => {
      state.heatmap.loading = false;
      state.heatmap.data = action.payload;
    });
    builder.addCase(fetchHeatmap.rejected, (state, action) => {
      state.heatmap.loading = false;
      console.error('Heatmap fetch error (handled with fallback):', action.error?.message);
    });

    // Callouts
    builder.addCase(fetchCallouts.pending, (state) => {
      state.callouts.loading = true;
      state.callouts.error = null;
    });
    builder.addCase(fetchCallouts.fulfilled, (state, action) => {
      state.callouts.loading = false;
      state.callouts.data = action.payload;
    });
    builder.addCase(fetchCallouts.rejected, (state, action) => {
      state.callouts.loading = false;
      console.error('Callouts fetch error (handled with fallback):', action.error?.message);
    });
  },
});

export const { clearDashboardData, toggleBriefMode, setBriefMode } = dashboardSlice.actions;

// Selectors
export const selectBriefMode = (state: RootState) => state.dashboard.briefMode;

export default dashboardSlice.reducer;
